/**
 * Extract the facility `care_slug` from Keycloak token claims.
 * The claim may be a string or a (multi-valued) array; anything else is ignored.
 * Pure — no browser APIs — so it is unit-testable.
 */
export function careSlugFromClaims(
  claims: Record<string, unknown>,
): string | null {
  const raw = claims["care_slug"];
  if (typeof raw === "string") return raw || null;
  if (Array.isArray(raw)) {
    const first = raw[0];
    return typeof first === "string" && first ? first : null;
  }
  return null;
}

export type AppEntryDecision =
  | { kind: "pass" }
  | { kind: "redirect"; url: string }
  | { kind: "support" };

/**
 * Decide what the app entry gate should do, given the current host and the
 * token claims. Pure — no browser APIs — so every branch is unit-testable.
 *
 * - Not the central host        -> pass the app through unchanged.
 * - Central host, no care_slug  -> support state (never a redirect loop).
 * - Central host, care_slug     -> redirect to the facility's partitioned host.
 */
export function resolveAppEntry(
  hostname: string,
  claims: Record<string, unknown>,
  appEntryHost: string,
  baseDomain: string,
): AppEntryDecision {
  if (hostname !== appEntryHost) return { kind: "pass" };
  const slug = careSlugFromClaims(claims);
  if (!slug) return { kind: "support" };
  return { kind: "redirect", url: `https://hms.${slug}.${baseDomain}` };
}
